import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

import { browserOnly } from '@farmersdog/utils';
import fastdom from 'fastdom';

import { AppNavHeight } from '../constants';
import { useIntersectionObserver } from './useIntersectionObserver';

function getScrollY(): number {
  return (
    browserOnly((window: Window) =>
      typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset
    ) ?? 0
  );
}

function getScrollProgress(): number {
  const scrollY = getScrollY();
  const scrollProgress = scrollY / AppNavHeight;
  return Math.min(scrollProgress, 1);
}

/**
 * Return an array of decimal points between 0 and 1 to use as
 * IntersectionObserver thresholds.
 *
 * See also:
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#thresholds
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Building_the_array_of_threshold_ratios
 */
function getThreshold(steps: number): number[] {
  return steps > -1
    ? [...(new Array(steps) as null[])].map((_, i) => i / steps)
    : [];
}

/** Returns the scroll progress to use for getting the scroll styles. */
export function useScrollProgress(target: RefObject<Element>): number {
  const [scrollProgress, setScrollProgress] = useState(getScrollProgress());

  const observerEntry = useIntersectionObserver(target, {
    threshold: getThreshold(AppNavHeight * 5),
    freezeOnceVisible: true,
  });

  useEffect(() => {
    if (!observerEntry || !target.current) {
      return;
    }
    const intersectionTop = observerEntry.intersectionRect.top;
    const boundingTop = observerEntry.boundingClientRect.top;
    let attachListener = false;
    if (boundingTop > 0) {
      // Content above the AppNav
      attachListener = intersectionTop > 0;
    } else {
      // Nothing above the AppNav
      attachListener = Math.abs(boundingTop) <= AppNavHeight;
    }

    const scrollListener = (): void => {
      fastdom.measure(() => {
        setScrollProgress(getScrollProgress());
      });
    };

    if (attachListener) {
      window.addEventListener('scroll', scrollListener);
    } else if (!attachListener) {
      // Set to 1 on detach to ensure we don't get stuck in a smaller progress value
      // This also fixes scroll position restore after a refresh
      setScrollProgress(1);
      window.removeEventListener('scroll', scrollListener);
    }

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [observerEntry, target]);

  return scrollProgress;
}
